// Job cards: each repair or service of a truck. The mechanic opens it and asks for parts from the stores; the workshop
// manager approves the parts and closes the card; the storekeeper issues only what was approved. A truck's cards are its history.
import { useState } from "react";
import { Page, Card, Btn, Table, Tr, Td, Field, TextIn, Loading, Pill, money } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan, useRegisters, Select, DateIn, Num, n, opts } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const TONE = { open: "amber", closed: "green", cancelled: "grey", pending: "amber", approved: "green", rejected: "red" };
const KINDS = [["repair", "Repair"], ["service", "Service"], ["breakdown", "Breakdown"], ["inspection", "Inspection / compliance"], ["tyres", "Tyres"]].map(([value, label]) => ({ value, label }));

export default function JobCards() {
  const [open, setOpen] = useState(null);
  return <Page title="Job cards">{open ? <Detail id={open} back={() => setOpen(null)} /> : <List go={setOpen} />}</Page>;
}

function List({ go }) {
  const can = useCan();
  const [status, setStatus] = useState("open");
  const { data, reload } = useLoad("/jobcards", { status: status || undefined });
  const regs = useRegisters(["trucks"]);
  const [f, setF] = useState(null);
  const save = async () => {
    const d = await act(() => api.post("/jobcards", { ...f, odometer: n(f.odometer) }), "Job card opened");
    if (d) { setF(null); reload(); go(d.id); }
  };
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {can("jobcard.open") && !f && <Btn kind="primary" onClick={() => setF({ kind: "repair", date: today() })}>+ Open a job card</Btn>}
        <Select empty="All" value={status} onChange={(v) => setStatus(v || "")} options={[{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }, { value: "cancelled", label: "Cancelled" }]} />
      </div>
      {f && (
        <Card title="New job card" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Truck"><Select value={f.truck_id} onChange={(v) => setF({ ...f, truck_id: v })} options={opts(regs.trucks, "plate")} /></Field>
            <Field label="What kind of work"><Select empty={null} value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={KINDS} /></Field>
            <Field label="Odometer now (km)"><Num value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} /></Field>
            <div className="sm:col-span-2"><Field label="What is wrong / what is to be done"><TextIn value={f.complaint} onChange={(v) => setF({ ...f, complaint: v })} /></Field></div>
            <Field label="Date"><DateIn value={f.date} onChange={(v) => setF({ ...f, date: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={save}>Open it</Btn><Btn onClick={() => setF(null)}>Cancel</Btn></div>
        </Card>)}
      {!data ? <Loading /> : (
        <>
          <Table minWidth={760} cols={[{ label: "Card" }, { label: "Truck" }, { label: "Work" }, { label: "Opened" }, { label: "Status" }, { label: "" }]}>
            {data.map((c) => (
              <Tr key={c.id}><Td className="font-medium">{c.no}</Td><Td>{c.truck}</Td><Td>{c.complaint}</Td>
                <Td>{fmtDate(c.date)}<div className="text-xs text-slate-500">{c.opened_name}</div></Td>
                <Td><Pill kind={TONE[c.status] || "grey"}>{c.status}</Pill> {c.pending_parts > 0 && <Pill kind="amber">parts waiting approval</Pill>}</Td>
                <Td><Btn onClick={() => go(c.id)}>Open</Btn></Td></Tr>))}
          </Table>
          {!data.length && <p className="mt-4 text-sm text-slate-500">No job cards here.</p>}
        </>)}
    </>
  );
}

function Detail({ id, back }) {
  const can = useCan();
  const { data: d, reload } = useLoad(`/jobcards/${id}`);
  const items = useLoad("/stores/items");
  const [ask, setAsk] = useState(null);
  const [out, setOut] = useState(null);
  const [close, setClose] = useState(null);
  if (!d || !items.data) return <Loading />;
  const isOpen = d.status === "open";
  const sendParts = async () => {
    const lines = ask.lines.filter((l) => l.item_id && n(l.qty) > 0).map((l) => ({ item_id: l.item_id, qty: n(l.qty) }));
    if (await act(() => api.post(`/jobcards/${id}/parts`, { lines, note: ask.note || "" }), "Parts requested - the workshop manager approves them")) { setAsk(null); reload(); }
  };
  const issue = async () => {
    const lines = out.rows.filter((r) => n(r.qty) > 0).map((r) => ({ request_id: r.request_id, line: r.line, qty: n(r.qty), old_returned: !!r.old_returned, no_old_reason: r.old_returned ? "" : r.no_old_reason || "" }));
    if (await act(() => api.post(`/jobcards/${id}/issue`, { collected_by: out.collected_by || "", lines }), "Issued")) { setOut(null); reload(); }
  };
  const byId = Object.fromEntries(items.data.map((i) => [i.id, i]));
  const startIssue = () => {
    const rows = [];
    d.requests.filter((r) => r.status === "approved").forEach((r) => r.lines.forEach((l, i) => { if (l.qty - l.issued > 0) rows.push({ request_id: r.id, line: i, name: l.name, left: l.qty - l.issued, qty: l.qty - l.issued, item_id: l.item_id }); }));
    setOut({ rows, collected_by: "" });
  };
  const doClose = async () => { if (await act(() => api.post(`/jobcards/${id}/close`, { work_done: close.work_done || "", odometer_out: n(close.odometer_out) }), "Job card closed")) { setClose(null); reload(); } };
  return (
    <>
      <div className="mt-3"><Btn onClick={back}>← All job cards</Btn></div>
      <Card title={`${d.no} · ${d.truck} · ${d.kind}`} className="mt-3">
        <p className="text-sm">{d.complaint}</p>
        <p className="mt-1 text-xs text-slate-500">Opened {fmtDate(d.date)} by {d.opened_name}{d.odometer ? ` · ${d.odometer} km` : ""} · <Pill kind={TONE[d.status] || "grey"}>{d.status}</Pill></p>
        {d.work_done && <p className="mt-2 text-sm"><b>Work done:</b> {d.work_done}</p>}
        <p className="mt-2 text-sm">Parts issued so far: <b>{money(d.parts_cost)}</b></p>
      </Card>
      <h3 className="mt-4 text-sm font-semibold text-slate-700">Parts requested</h3>
      <Table minWidth={620} cols={[{ label: "Parts" }, { label: "Value", align: "right" }, { label: "By" }, { label: "Status" }]}>
        {d.requests.map((r) => (
          <Tr key={r.id}><Td>{r.lines.map((l) => `${l.qty} ${l.unit} ${l.name}${l.issued ? ` (${l.issued} issued)` : ""}`).join("; ")}</Td><Td right>{money(r.value)}</Td><Td>{r.requested_name}</Td>
            <Td><Pill kind={TONE[r.status] || "grey"}>{r.status}</Pill></Td></Tr>))}
      </Table>
      {!d.requests.length && <p className="text-sm text-slate-500">None yet.</p>}
      {d.issued.length > 0 && (
        <>
          <h3 className="mt-4 text-sm font-semibold text-slate-700">Issued from the stores</h3>
          <Table minWidth={620} cols={[{ label: "Date" }, { label: "Part" }, { label: "Qty", align: "right" }, { label: "Collected by" }]}>
            {d.issued.map((m) => (
              <Tr key={m.id}><Td>{fmtDate(m.date)}</Td><Td>{m.name}{(m.flags || []).map((f, i) => <div key={i} className="text-xs text-red-600">{f}</div>)}</Td><Td right>{-m.qty}</Td><Td>{m.collected_by}</Td></Tr>))}
          </Table>
        </>)}
      {isOpen && !ask && !out && !close && (
        <div className="mt-4 flex flex-wrap gap-2">
          {can("jobcard.open", "jobcard.approve") && <Btn onClick={() => setAsk({ lines: [{}] })}>Ask for parts</Btn>}
          {can("stores.issue") && d.requests.some((r) => r.status === "approved") && <Btn onClick={startIssue}>Issue approved parts</Btn>}
          {can("jobcard.close") && <Btn kind="primary" onClick={() => setClose({})}>Close the job card</Btn>}
        </div>)}
      {ask && (
        <Card title="Ask the stores for parts" className="mt-3">
          {ask.lines.map((l, i) => (
            <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[1fr_120px]">
              <Select value={l.item_id} options={opts(items.data, (x) => `${x.code} · ${x.name} (${x.on_hand} in stock)`)} onChange={(v) => setAsk({ ...ask, lines: ask.lines.map((x, j) => (j === i ? { ...x, item_id: v } : x)) })} />
              <Num placeholder="Qty" value={l.qty} onChange={(v) => setAsk({ ...ask, lines: ask.lines.map((x, j) => (j === i ? { ...x, qty: v } : x)) })} />
            </div>))}
          <Btn onClick={() => setAsk({ ...ask, lines: [...ask.lines, {}] })}>+ Another part</Btn>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={sendParts}>Send for approval</Btn><Btn onClick={() => setAsk(null)}>Cancel</Btn></div>
        </Card>)}
      {out && (
        <Card title="Issue approved parts" className="mt-3">
          <Field label="Collected by"><TextIn value={out.collected_by} onChange={(v) => setOut({ ...out, collected_by: v })} /></Field>
          {out.rows.map((r, i) => {
            const it = byId[r.item_id];
            const set = (k, v) => setOut({ ...out, rows: out.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
            return (
              <div key={i} className="mt-3 rounded-md border border-slate-200 p-3">
                <div className="grid gap-2 sm:grid-cols-[1fr_120px]"><span className="text-sm font-medium">{r.name} <span className="text-slate-500">(up to {r.left})</span></span><Num placeholder="Qty" value={r.qty} onChange={(v) => set("qty", v)} /></div>
                {it?.return_old && (
                  <div className="mt-2 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!r.old_returned} onChange={(e) => set("old_returned", e.target.checked)} />The old part has been handed back</label>
                    {!r.old_returned && <div className="mt-1"><TextIn placeholder="No old part - why?" value={r.no_old_reason} onChange={(v) => set("no_old_reason", v)} /></div>}
                  </div>)}
              </div>);
          })}
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={issue}>Issue</Btn><Btn onClick={() => setOut(null)}>Cancel</Btn></div>
        </Card>)}
      {close && (
        <Card title="Close the job card" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2"><Field label="What was done"><TextIn value={close.work_done} onChange={(v) => setClose({ ...close, work_done: v })} /></Field></div>
            <Field label="Odometer out (km)"><Num value={close.odometer_out} onChange={(v) => setClose({ ...close, odometer_out: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={doClose}>Close it</Btn><Btn onClick={() => setClose(null)}>Cancel</Btn></div>
        </Card>)}
      {isOpen && !d.issued.length && <div className="mt-4"><Btn onClick={async () => { if (await act(() => api.post(`/jobcards/${id}/cancel`), "Cancelled")) back(); }}>Cancel this card</Btn></div>}
    </>
  );
}
